// Soulstack Studio site root: the full route table from the design prototype
// (marketing, docs, showcase, play, auth, app, project workspace) on real
// history routing. The editor is the 13-mode shell at /app/editor/:id/:mode;
// the legacy /p/:id engine-editor shell it replaced was removed in SH2.2.
import { type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';

import { useAuth } from './auth/AuthProvider';
import { ScreenNotFound, SiteFrame, publicRoutes } from './PublicSite';

import { ScreenAccount, ScreenResetPassword, ScreenVerifyEmail, ScreenWelcome } from './account/screens-account';
import { ScreenAuth, ScreenComingSoon, ScreenDashboard } from './app/screens-app';
import { SIGNUP_OPEN } from './config/studioStatus';
import { consumeSignupIntent } from './services/auth';
import { DOC_REDIRECTS } from './docs/docsContent';
import { ScreenGameLanding, ScreenPlayRuntime } from './play/screens-play';
import { ScreenDevProfile } from './showcase/screens-showcase';
import { ScreenGameRoute } from './showcase/screens-game-route';

import {
  ScreenProjectAssets,
  ScreenProjectOverview,
  WorkspaceProvider,
} from './project/screens-project';
import {
  ScreenProjectPlayers,
  ScreenProjectPublish,
  ScreenProjectSettings,
  ScreenProjectVersions,
} from './project/screens-project-ops';
import { ScreenProjectWiki } from './wiki/screens-project-wiki';
import { ScreenWikiHome, ScreenWikiPage, ScreenWikisDirectory } from './wiki/screens-wiki-public';

import { BakeThumbnails } from './dev/BakeThumbnails';
import { EditorFixtureRoute } from './dev/EditorFixtureRoute';
import { EditorRoute } from './editor/EditorHost';

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="studio-loading">Loading…</div>;
  if (!user) return <Navigate to="/signin" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  // A Google sign-up leaves the SPA for the OAuth round trip and lands back
  // here on a fresh page load once Cognito resolves it - consumeSignupIntent
  // is how that's told apart from an ordinary Google sign-in, which lands on
  // this same guard. See markSignupIntent's call site (ScreenAuth) for why.
  if (!loading && user) return <Navigate to={consumeSignupIntent() ? '/app/welcome' : '/app'} replace />;
  return <>{children}</>;
}

/* Route param adapters for screens that take plain props. */
function DevProfileRoute() {
  const { slug = '' } = useParams();
  return <ScreenDevProfile slug={slug} />;
}
function GameLandingRoute() {
  const { slug = '' } = useParams();
  return <ScreenGameLanding slug={slug} />;
}
function PlayRuntimeRoute() {
  const { slug = '' } = useParams();
  return <ScreenPlayRuntime key={slug} slug={slug} />;
}
function WikiHomeRoute() {
  const { slug = '' } = useParams();
  return <ScreenWikiHome key={slug} slug={slug} />;
}
function WikiPageRoute() {
  const { slug = '', pageSlug = '' } = useParams();
  return <ScreenWikiPage key={`${slug}/${pageSlug}`} slug={slug} pageSlug={pageSlug} />;
}

function SiteRoutes() {
  return (
    <Routes>
      {/* Marketing, docs and legal: pre-rendered at build time (PublicSite.tsx) */}
      {publicRoutes()}
      <Route path="/showcase/:slug" element={<ScreenGameRoute />} />
      {import.meta.env.DEV && <Route path="/dev/bake-thumbs" element={<BakeThumbnails />} />}
      {import.meta.env.DEV && <Route path="/dev/editor-fixture/:mode?" element={<EditorFixtureRoute />} />}
      <Route path="/dev/:slug" element={<DevProfileRoute />} />

      {/* The old docs addresses */}
      {Object.entries(DOC_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}

      {/* Play surface */}
      <Route path="/play/:slug" element={<GameLandingRoute />} />
      <Route path="/play/:slug/live" element={<PlayRuntimeRoute />} />

      {/* Wiki surface */}
      <Route path="/wikis" element={<ScreenWikisDirectory />} />
      <Route path="/wiki/:slug" element={<WikiHomeRoute />} />
      <Route path="/wiki/:slug/:pageSlug" element={<WikiPageRoute />} />

      {/* Auth */}
      <Route
        path="/signin"
        element={
          <RedirectIfAuthed>
            <ScreenAuth mode="signin" fill />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfAuthed>
            {SIGNUP_OPEN ? <ScreenAuth mode="signup" fill /> : <ScreenComingSoon fill />}
          </RedirectIfAuthed>
        }
      />
      <Route path="/verify" element={<ScreenVerifyEmail />} />
      <Route path="/reset" element={<ScreenResetPassword />} />

      {/* App (authenticated) */}
      <Route
        path="/app"
        element={
          <RequireAuth>
            <ScreenDashboard fill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/welcome"
        element={
          <RequireAuth>
            <ScreenWelcome />
          </RequireAuth>
        }
      />
      <Route
        path="/app/account"
        element={
          <RequireAuth>
            <ScreenAccount />
          </RequireAuth>
        }
      />
      <Route
        path="/app/project/:id"
        element={
          <RequireAuth>
            <WorkspaceProvider />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<ScreenProjectOverview />} />
        <Route path="assets" element={<ScreenProjectAssets />} />
        <Route path="publish" element={<ScreenProjectPublish />} />
        <Route path="versions" element={<ScreenProjectVersions />} />
        <Route path="wiki" element={<ScreenProjectWiki />} />
        <Route path="players" element={<ScreenProjectPlayers />} />
        <Route path="settings" element={<ScreenProjectSettings />} />
      </Route>
      <Route
        path="/app/editor/:id/:mode?"
        element={
          <RequireAuth>
            <EditorRoute />
          </RequireAuth>
        }
      />

      <Route path="*" element={<ScreenNotFound />} />
    </Routes>
  );
}

/** Site root: auth context + history router + the design's route map. */
export function SiteApp() {
  return (
    <SiteFrame router={BrowserRouter}>
      <SiteRoutes />
    </SiteFrame>
  );
}
